var _excluded = ["className", "clearIcon", "defaultValue", "disabled", "error", "helperText", "helperTextState", "helperTextVisibility", "i18n", "id", "label", "multiple", "name", "onChange", "onClear", "onScrollEnd", "onSearch", "options", "optionsOrientation", "placeholder", "placement", "portalContainer", "preselectedOptions", "readOnly", "required", "scrollEndOffset", "searchDelay", "separateLabel", "showSearch", "value", "visibleOptions"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import React from 'react';
import { useId } from '@reach/auto-id';
import { useTheme } from '@veneer/theme';
import { FormControl, FormLabel, FormHelperText } from '../form_utils';
import SelectInput from './select_input';
import propTypes from './prop_types';
import selectI18n from './i18n';
import { jsx as ___EmotionJSX } from "@emotion/core";
var customStyles = _ref => {
  var {
    focused,
    popoverOpen,
    separateLabel,
    theme
  } = _ref;
  var borderWidth = focused || popoverOpen ? '2px' : '1px';
  return _objectSpread({
    color: theme.palette.foreground.medium,
    fontFamily: theme.typography.family0,
    fontSize: focused ? theme.fontSizeThree : '16px',
    lineHeight: theme.typography.lineHeight2,
    position: 'relative',
    width: '100%'
  }, !separateLabel && {
    'label + div > div[role="combobox"]': _objectSpread(_objectSpread({
      padding: "calc(".concat(theme.orbitalThree, " - ").concat(borderWidth, ")"),
      paddingBottom: "calc(".concat(theme.orbitalOne, " - ").concat(borderWidth, ")"),
      paddingTop: "calc(".concat(theme.orbitalTwo, " + ").concat(theme.typography.lineHeight0, " - ").concat(borderWidth, ")")
    }, theme.orbitalOne === 0 && {
      marginBottom: '-2px',
      paddingBottom: 0,
      paddingTop: "calc(".concat(theme.layout.size3, " + 1px - ").concat(borderWidth, ")")
    }), {}, {
      '> .vn-select__placeholder': {
        opacity: focused ? '1' : '0'
      }
    }),
    'label[data-focused="false"]': {
      margin: 0
    }
  });
};
function getNewValue(value, newValue) {
  var multiple = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var valueArray = [];
  if (multiple) {
    var index = value.indexOf(newValue.value);
    if (index !== -1) {
      valueArray = value.filter((v, i) => i !== index);
    } else {
      valueArray = [...value, newValue.value];
    }
  } else {
    valueArray = [newValue.value];
  }
  return valueArray;
}
var Select = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  var {
      className,
      clearIcon = true,
      defaultValue: defaultValueProp = [],
      disabled = false,
      error = false,
      helperText,
      helperTextState,
      helperTextVisibility = 'auto',
      i18n: i18nProp,
      id: idProp,
      label,
      multiple = false,
      name,
      onChange,
      onClear,
      onScrollEnd,
      onSearch,
      options,
      optionsOrientation = [],
      placeholder,
      placement,
      portalContainer,
      preselectedOptions = [],
      readOnly = false,
      required = false,
      scrollEndOffset = 0,
      searchDelay,
      separateLabel = false,
      showSearch = false,
      value: valueProp,
      visibleOptions = 10
    } = _ref2,
    other = _objectWithoutProperties(_ref2, _excluded);
  var theme = useTheme();
  var autoId = useId();
  var id = idProp || "vn-select-".concat(autoId);
  var i18n = React.useMemo(() => _objectSpread(_objectSpread({}, selectI18n), i18nProp), [i18nProp]);
  var {
    current: isControlled
  } = React.useRef(valueProp != null);
  var [defaultValue, setDefaultValue] = React.useState(defaultValueProp);
  var [open, setOpen] = React.useState(false);
  var value = isControlled ? valueProp : defaultValue;
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      if (Array.isArray(optionsOrientation) && optionsOrientation.length > 0) {
        console.error(["@veneer/core: The value [".concat(optionsOrientation, "] supplied to `Select.optionsOrientation` was deprecated."), "Use the `placement` property."].join('\n'));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  }
  var handleChange = React.useCallback(newValue => {
    if (!isControlled) {
      setDefaultValue(prevValue => getNewValue(prevValue, newValue, multiple));
    }
    onChange === null || onChange === void 0 || onChange(newValue);
  }, [isControlled, multiple, onChange]);
  var handleUpdateOpen = React.useCallback(newValue => {
    setOpen(newValue);
  }, []);
  var handleClear = React.useCallback(() => {
    if (!isControlled) {
      setDefaultValue([]);
    }
    onClear === null || onClear === void 0 || onClear();
  }, [isControlled, onClear]);
  var getMaxWidthOffset = React.useCallback(() => {
    var hasValue = _value => _value && Array.isArray(_value) && _value.length > 0 && _value[0] !== undefined;
    var isShrunken = !readOnly && !disabled && clearIcon && hasValue(value);
    var offsetChevronIcon = theme.orbitalSeven;
    var offsetClearIcon = theme.orbitalEight;
    return isShrunken ? "".concat(offsetChevronIcon, " + ").concat(offsetClearIcon) : offsetChevronIcon;
  }, [clearIcon, disabled, readOnly, theme, value]);
  var helperTextId = helperText && id ? "".concat(id, "-helper-text") : undefined;
  var labelId = label && id ? "".concat(id, "-label") : undefined;
  return ___EmotionJSX(FormControl, {
    className: className,
    customStyle: customStyles,
    disabled: disabled,
    error: error,
    helperTextState: helperTextState,
    popoverOpen: open,
    required: required,
    separateLabel: separateLabel
  }, label && ___EmotionJSX(FormLabel, {
    htmlFor: id,
    id: labelId,
    maxWidthOffset: getMaxWidthOffset()
  }, label), ___EmotionJSX(SelectInput, _extends({
    clearIcon: clearIcon,
    i18n: i18n,
    id: id,
    labelId: labelId,
    multiple: multiple,
    name: name,
    onChange: handleChange,
    onClear: handleClear,
    onScrollEnd: onScrollEnd,
    onSearch: onSearch,
    onUpdateOpen: handleUpdateOpen,
    open: open,
    options: options,
    optionsOrientation: optionsOrientation,
    placeholder: placeholder,
    placement: placement,
    portalContainer: portalContainer,
    preselectedOptions: preselectedOptions,
    readOnly: readOnly,
    ref: ref,
    scrollEndOffset: scrollEndOffset,
    searchDelay: searchDelay,
    showSearch: showSearch,
    value: value,
    visibleOptions: visibleOptions
  }, other)), helperText && ___EmotionJSX(FormHelperText, {
    helperTextVisibility: helperTextVisibility,
    id: helperTextId
  }, helperText));
});
Select.propTypes = propTypes;
Select.displayName = 'Select';
export default Select;